/** @jsxImportSource @emotion/react */
import { useMemo } from 'react';
import styled from '@emotion/styled';
import Skeleton from '@mui/material/Skeleton';
import type { Chat } from '@trinity/chat';
import { ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import { groupBy } from '@trinity/chat-components/utils';
import { MessageResponse } from './Message.Response';
import { SegmentationFooter } from './Segmentation.Footer';

type Props = {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationComplete;
  citations: Chat.Citation[];
};

type Finding = {
  dimension: string;
  tagId: number;
  response: string;
  citations: Chat.Citation[];
};

export const SegmentationResult = (props: Props) => {

  const isCompleted = useMemo(() => {
    return props.value.findings.every(finding => !!finding.response);
  }, [props.value.findings]);

  const tagMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; occurrences: number }>();
    props.value.availableTags.forEach(tag => {
      map.set(tag.id, tag);
    });
    return map;
  }, [props.value.availableTags]);

  // Group findings by dimension (each dimension has findings for each tag)
  const resultsByDimension = useMemo(() => {
    const grouped = new Map<string, Array<{
      tagId: number;
      tagName: string;
      response: string;
      citations: Chat.Citation[];
      identifier: string;
    }>>();

    props.value.findings.forEach(finding => {
      const tag = tagMap.get(finding.tagId);
      if (!tag) {
        return;
      }

      if (!grouped.has(finding.dimension)) {
        grouped.set(finding.dimension, []);
      }

      const dimensionGroup = grouped.get(finding.dimension);
      if (dimensionGroup) {
        dimensionGroup.push({
          tagId: finding.tagId,
          tagName: tag.name,
          response: finding.response,
          citations: finding.citations,
          identifier: `${finding.dimension}-${finding.tagId}`,
        });
      }
    });

    // Convert to array format matching sentiment's structure
    return Array.from(grouped.entries()).map(([dimension, findings]) => ({
      identifier: dimension,
      title: dimension,
      dimension,
      findings: findings.map(f => ({
        identifier: f.identifier,
        label: f.tagName,
        response: {
          type: ChatResponseType.Markdown,
          value: f.response,
        },
        citations: f.citations,
      })),
    }));
  }, [props.value.findings, tagMap]);

  // Aggregate and deduplicate citations from all findings
  const aggregatedCitations = useMemo(() => {
    const allCitations = props.value.findings.flatMap(f => f.citations);

    return Object.entries(groupBy(allCitations, c => c.id)).map(([id, citations]) => {
      return {
        ...citations[0],
        snippets: Object.values(
          groupBy(citations.flatMap(c => c.snippets), s => s.id),
        )
          .map(x => x[0])
          .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
      };
    }).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
  }, [props.value.findings]);

  return (
    <SegmentationFooter
      results={resultsByDimension}
      citations={aggregatedCitations}
      isCompleted={isCompleted}>
      <Root>
        <SystemHeaderCopy>
          Ok, this will take a few moments! You can wait to view the results, or if you navigate away the results will still be available when you return.
        </SystemHeaderCopy>
        <Results>
          {resultsByDimension.map(result => (
            <Result key={result.identifier}>
              <ResultLabel>{result.title}</ResultLabel>
              <Findings>
                {result.findings.map(finding => (
                  <Finding key={finding.identifier}>
                    {finding.response.value ? (
                      <>
                        <FindingLabel>{finding.label}</FindingLabel>
                        <MessageResponse
                          item={finding.response}
                          queryIdentifier={props.queryIdentifier}
                          state={{}}
                          status={ChatQueryStatus.Complete}
                        />
                      </>
                    ) : (
                      <StyledLoader variant="text" />
                    )}
                  </Finding>
                ))}
              </Findings>
            </Result>
          ))}
        </Results>
      </Root>
    </SegmentationFooter>
  );
};

export const SegmentationResultUserMessage = (props: Props) => {
  // Build a map of tagId -> tag for quick lookups
  const tagMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; occurrences: number }>();
    props.value.availableTags.forEach(tag => {
      map.set(tag.id, tag);
    });
    return map;
  }, [props.value.availableTags]);

  const selectedTags = useMemo(() => {
    return props.value.selectedTags
      .map(tagId => tagMap.get(tagId))
      .filter((tag): tag is { id: number; name: string; occurrences: number } => tag !== undefined);
  }, [props.value.selectedTags, tagMap]);

  return (
    <>
      <UserHeaderCopy>
        {`Yes, let's analyze `}
        {selectedTags.map((tag, i) => (
          <span key={tag.id}>
            <b>{tag.name}</b>
            {i !== selectedTags.length - 1 ? `, ` : null}
          </span>
        ))}
        {` tagged data on the following dimensions:`}
      </UserHeaderCopy>
      <Dimensions>
        {props.value.dimensions.map((dimension, idx) => (
          <DimensionItem key={idx}>{dimension}</DimensionItem>
        ))}
      </Dimensions>
    </>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const UserHeaderCopy = styled.div`
  box-sizing: border-box;
  margin-bottom: 10px;
`;

const SystemHeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${props => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const Dimensions = styled.ul`
  padding-inline-start: 1em;
  margin: 0 0 20px 0;

  li + li {
    margin-top: 10px;
  }
`;

const DimensionItem = styled.li``;

const Results = styled.div``;

const Result = styled.div`
  :not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ResultLabel = styled.div`
  font-family: ${props => props.theme.fonts.bold};
  margin-bottom: 10px;
`;

const StyledLoader = styled(Skeleton)`
  background-color: ${props => props.theme.palette.green.light3};
  height: 40px;
  width: 65%;
`;

const Findings = styled.div`
  box-sizing: border-box;
`;

const Finding = styled.div`
  :not(:last-child) {
    margin-bottom: 20px;
  }
`;

const FindingLabel = styled.div`
  font-family: ${props => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;
